import groups from "./api/groups.json" with { type: "json" };
import {
	buildAuthGroup,
	loginAdapter,
	loginEnv,
	loginExamples,
	loginHint,
	loginOptions,
	loginOutput,
	logoutAdapter,
	logoutOptions,
	logoutOutput,
} from "./auth/commands";
import { buildAppGroup } from "./apps/commands";
import {
	quickstartOptions,
	quickstartOutput,
	runQuickstart,
} from "./journey/quickstart";
import {
	runUpgrade,
	upgradeOptions,
	upgradeOutput,
} from "./lib/update/command";

type CliRegistration = {
	command: (...args: any[]) => unknown;
};

type HandwrittenGroup = {
	name: string;
	description: string;
	section: "get-started" | "auth";
	register: (cli: CliRegistration) => unknown | Promise<unknown>;
};

export const HANDWRITTEN_GROUPS = [
	{
		name: "quickstart",
		description: "Start here — choose or create the business used by the CLI",
		section: "get-started",
		register: (cli: CliRegistration) =>
			cli.command("quickstart", {
				description: "Set up the business account the CLI should use",
				hint: "OAuth users can choose or create a business. API keys are tied to one business. Agents and scripts should pass --account_id or --business_name when the choice is ambiguous.",
				options: quickstartOptions,
				output: quickstartOutput,
				examples: [
					{ description: "Choose or create a business interactively" },
					{
						options: { account_id: "biz_xxx" },
						description: "Choose an existing business account",
					},
					{
						options: { business_name: "Acme" },
						description: "Create and select a new business account",
					},
				],
				outputPolicy: "agent-only",
				run: (c: Parameters<typeof runQuickstart>[0]) => runQuickstart(c),
			}),
	},
	{
		name: "apps",
		description: "Build and deploy fully-hosted web apps (*.whop.app)",
		section: "get-started",
		register: async (cli: CliRegistration) =>
			cli.command(await buildAppGroup()),
	},
	{
		name: "upgrade",
		description: "Update the CLI to the latest version",
		section: "get-started",
		register: (cli: CliRegistration) =>
			cli.command("upgrade", {
				description: "Update the whop CLI to the latest version",
				options: upgradeOptions,
				output: upgradeOutput,
				examples: [
					{ description: "Update to the latest version" },
					{
						options: { check: true },
						description: "Check for a newer version without installing",
					},
				],
				run: (c: Parameters<typeof runUpgrade>[0]) => runUpgrade(c),
			}),
	},
	{
		name: "auth",
		description: "Manage authentication",
		section: "auth",
		register: (cli: CliRegistration) => cli.command(buildAuthGroup()),
	},
	{
		name: "login",
		description: "Sign in to Whop",
		section: "auth",
		register: (cli: CliRegistration) =>
			cli.command("login", {
				description: "Log in to Whop (alias for `auth login`)",
				options: loginOptions,
				env: loginEnv,
				output: loginOutput,
				examples: loginExamples,
				hint: loginHint,
				run: (c: Parameters<typeof loginAdapter>[0]) => loginAdapter(c),
			}),
	},
	{
		name: "logout",
		description: "Sign out",
		section: "auth",
		register: (cli: CliRegistration) =>
			cli.command("logout", {
				description: "Log out (alias for `auth logout`)",
				options: logoutOptions,
				output: logoutOutput,
				run: (c: Parameters<typeof logoutAdapter>[0]) => logoutAdapter(c),
			}),
	},
] satisfies HandwrittenGroup[];

export const HANDWRITTEN_GROUP_NAMES = new Set(
	HANDWRITTEN_GROUPS.map(({ name }) => name),
);

export const API_GROUPS = groups.filter(
	({ name }) => !HANDWRITTEN_GROUP_NAMES.has(name),
);

export const COMMAND_GROUPS = [...API_GROUPS, ...HANDWRITTEN_GROUPS] as const;

export async function registerHandwrittenGroups(cli: CliRegistration) {
	for (const group of HANDWRITTEN_GROUPS) await group.register(cli);
}
